import { useState } from 'react';
import {
  CATEGORIES, groupByCategory, getExpiringItems,
  getExpiryStatus, formatDate, generateRecipeText
} from '../utils/helpers';
import { EditItemModal } from '../components/EditItemModal';

export function FridgeView({
  items, loading, selectedIds, onToggleSelect,
  onConsume, onDelete, onRefresh, onClearSelection, showToast, onNavigate
}) {
  const [editItem, setEditItem] = useState(null);

  const expiringItems = getExpiringItems(items, 3);
  const grouped = groupByCategory(items);
  const selectedCount = selectedIds.size;

  // Rezept-Text kopieren
  const handleCopyRecipe = async () => {
    const selected = items.filter(i => selectedIds.has(i.id));
    if (selected.length === 0) return;
    const text = generateRecipeText(selected);
    try {
      await navigator.clipboard.writeText(text);
      showToast('Rezept-Text kopiert! Jetzt in ChatGPT o.ä. einfügen.');
      onClearSelection();
    } catch {
      showToast('Kopieren fehlgeschlagen');
    }
  };

  if (loading && items.length === 0) {
    return (
      <div className="content">
        <div className="loading-overlay">
          <div className="spinner" />
          <span>Kühlschrank wird geladen...</span>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="content">
        <div className="empty-state">
          <div className="empty-state-icon">🧊</div>
          <div className="empty-state-title">Kühlschrank ist leer</div>
          <div className="empty-state-text">
            Lade deinen ersten Kassenbeleg hoch, um Produkte hinzuzufügen.
          </div>
          <button className="btn btn-primary" onClick={() => onNavigate('upload')}>
            📄 Beleg hochladen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      {/* Ablauf-Warnung */}
      {expiringItems.length > 0 && (
        <div className="expiry-banner">
          <span className="expiry-banner-icon">⚠️</span>
          <div className="expiry-banner-text">
            <span className="expiry-banner-count">{expiringItems.length} Produkt{expiringItems.length !== 1 ? 'e' : ''}</span>
            {' '}laufen in den nächsten 3 Tagen ab!
          </div>
        </div>
      )}

      {/* Pull to Refresh Hinweis */}
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <button
          className="btn btn-outline btn-sm"
          onClick={onRefresh}
          style={{ fontSize: 12 }}
        >
          ↻ Aktualisieren
        </button>
      </div>

      {/* Items nach Kategorie */}
      {grouped.map(([category, catItems]) => {
        const cat = CATEGORIES[category] || CATEGORIES.sonstiges;
        return (
          <div key={category} className="category-section">
            <div className="category-header">
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
              <span className="category-count">{catItems.length}</span>
            </div>
            {catItems.map(item => {
              const expiry = getExpiryStatus(item.expiry_date);
              const isSelected = selectedIds.has(item.id);
              let cardClass = 'item-card';
              if (isSelected) cardClass += ' selected';
              else if (expiry.days !== null && expiry.days <= 0) cardClass += ' expired';
              else if (expiry.days !== null && expiry.days <= 3) cardClass += ' expiring-soon';

              return (
                <div key={item.id} className={cardClass}>
                  {/* Checkbox für Rezept-Auswahl */}
                  <div
                    className={`item-checkbox ${isSelected ? 'checked' : ''}`}
                    onClick={() => onToggleSelect(item.id)}
                  >
                    {isSelected && '✓'}
                  </div>

                  {/* Info */}
                  <div className="item-info" onClick={() => setEditItem(item)}>
                    <div className="item-name">{item.name}</div>
                    <div className="item-meta">
                      {item.quantity > 1 && (
                        <span>{item.quantity} {item.unit}</span>
                      )}
                      {item.expiry_date && (
                        <>
                          <span>·</span>
                          <span className={`item-expiry ${expiry.class}`}>
                            {expiry.label}
                          </span>
                          <span style={{ color: 'var(--gray-400)' }}>
                            ({formatDate(item.expiry_date)})
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Aktionen */}
                  <div className="item-actions">
                    <button
                      className="item-action-btn eat"
                      onClick={() => onConsume(item.id)}
                      title="Aufgegessen"
                    >
                      ✓
                    </button>
                    <button
                      className="item-action-btn"
                      onClick={() => onDelete(item.id)}
                      title="Entfernen"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Auswahl-Leiste für Rezept-Export */}
      {selectedCount > 0 && (
        <div className="selection-bar">
          <span className="selection-count">
            {selectedCount} ausgewählt
          </span>
          <div className="selection-actions">
            <button className="btn btn-sm btn-outline" onClick={onClearSelection}
              style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
              Abbrechen
            </button>
            <button className="btn btn-sm btn-success" onClick={handleCopyRecipe}>
              📋 Rezept kopieren
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editItem && (
        <EditItemModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSave={async (updates) => {
            await import('../utils/api').then(m => m.api.updateItem(editItem.id, updates));
            onRefresh();
            setEditItem(null);
            showToast('Gespeichert');
          }}
        />
      )}
    </div>
  );
}
